import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Calendar as BigCalendar, dateFnsLocalizer, Views } from "react-big-calendar";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import "@/components/calendar/calendar.css";
import { format, parse, startOfWeek, getDay, addDays, addMonths, startOfDay } from "date-fns";
import { enGB } from "date-fns/locale";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import {
    ChevronLeft, ChevronRight, Plus, Search, X, CalendarDays, Users,
} from "lucide-react";

import InstallerShell from "@/components/installer/InstallerShell";
import OperationsShell from "@/components/operations/OperationsShell";
import { PageContainer, PageHeader } from "@/components/layout/PageLayout";
import { api, formatApiError } from "@/lib/api";
import CalendarAppointmentForm from "@/components/calendar/CalendarAppointmentForm";
import CalendarAppointmentDetail from "@/components/calendar/CalendarAppointmentDetail";
import {
    parseCalendarInitFromUrl,
    encodeCalendarUrl,
    CALENDAR_UNASSIGNED_SENTINEL,
} from "@/lib/navLinks";
import ClashWarningDialog from "@/components/calendar/ClashWarningDialog";
import AvailabilityWarningDialog from "@/components/calendar/AvailabilityWarningDialog";
import BlackoutConfirmationDialog from "@/components/calendar/BlackoutConfirmationDialog";
import { useAuth } from "@/context/AuthContext";
import { useBulkSelection } from "@/hooks/useBulkSelection";
import { BulkCheckbox } from "@/components/bulk/BulkCheckbox";
import BulkActionBar from "@/components/bulk/BulkActionBar";
import BulkDeleteModal from "@/components/bulk/BulkDeleteModal";
import { reportBulkDeleteResult } from "@/components/bulk/reportBulkDelete";
import { ChevronDown } from "lucide-react";

const locales = { "en-GB": enGB };
const localizer = dateFnsLocalizer({
    format, parse, startOfWeek: (d) => startOfWeek(d, { weekStartsOn: 1 }), getDay, locales,
});
const DnDCalendar = withDragAndDrop(BigCalendar);

const VIEW_KEY = "solarflow_calendar_view";
const DATE_KEY = "solarflow_calendar_date";

const STATUS_LABEL = {
    scheduled: "Scheduled", confirmed: "Confirmed", in_progress: "In progress",
    completed: "Completed", cancelled: "Cancelled",
};

/** Compute a fetch window that covers the visible period + a small buffer. */
function windowFor(view, date) {
    if (view === Views.MONTH) {
        const start = startOfDay(addDays(date, -35));
        const end = startOfDay(addDays(date, 45));
        return { from: start.toISOString(), to: end.toISOString() };
    }
    if (view === Views.WEEK) {
        const start = startOfWeek(date, { weekStartsOn: 1 });
        return {
            from: addDays(start, -3).toISOString(),
            to: addDays(start, 10).toISOString(),
        };
    }
    // Day
    return {
        from: startOfDay(addDays(date, -1)).toISOString(),
        to: startOfDay(addDays(date, 2)).toISOString(),
    };
}

function rangeTitle(view, date) {
    if (view === Views.DAY)   return format(date, "EEEE d MMMM yyyy");
    if (view === Views.MONTH) return format(date, "MMMM yyyy");
    const start = startOfWeek(date, { weekStartsOn: 1 });
    const end = addDays(start, 6);
    if (start.getMonth() === end.getMonth()) {
        return `${format(start, "d")}–${format(end, "d MMMM yyyy")}`;
    }
    return `${format(start, "d MMM")} – ${format(end, "d MMM yyyy")}`;
}

/** Bright-vs-dark decision for readable text on any engineer accent. */
function readableOn(hex) {
    if (!hex) return "#131B2F";
    const c = hex.replace("#", "");
    const r = parseInt(c.substring(0, 2), 16);
    const g = parseInt(c.substring(2, 4), 16);
    const b = parseInt(c.substring(4, 6), 16);
    const yiq = (r * 299 + g * 587 + b * 114) / 1000;
    return yiq >= 155 ? "#131B2F" : "#ffffff";
}

/** Map an appointment/sales-action doc → react-big-calendar event object.
 *
 * DEV-019F Calendar UI completion · Backend Calendar aggregation now
 * emits two row kinds:
 *   • kind="appointment"   — existing visit/appointment rows.
 *   • kind="sales_action"  — active dated Sales Actions surfaced from
 *                            ``projects.sales_actions[]`` (see
 *                            ``routers/calendar.py::_collect_sales_action_events``).
 *
 * We preserve `resource` verbatim so downstream (click handler,
 * event-style-getter, filter chip) can branch on `resource.kind`
 * without duplicating parsing logic.
 *
 * DEV-019F UAT · All-day date-only Sales Actions MUST be a true
 * single-day all-day event. The backend emits `start_at =
 * YYYY-MM-DDT00:00:00+00:00` and `end_at = <next day>T00:00:00+00:00`
 * (react-big-calendar treats the all-day end as EXCLUSIVE), but we
 * still have to keep the browser timezone from shifting the date.
 * We rebuild start/end from the canonical `due_date` (YYYY-MM-DD)
 * as a LOCAL midnight so the event never leaks into the previous
 * or following day when the browser is west of UTC (WEEK/MONTH)
 * or east of UTC (DAY all-day gutter).
 */
function toEvent(a) {
    const isSalesAction = a?.kind === "sales_action";
    let start;
    let end;
    if (isSalesAction && a?.all_day && a?.due_date) {
        const parts = String(a.due_date).split("-");
        if (parts.length === 3) {
            const y = parseInt(parts[0], 10);
            const m = parseInt(parts[1], 10);
            const d = parseInt(parts[2], 10);
            // Local midnight (start of the due day) for BOTH start
            // and end — RBC renders a single-day all-day event when
            // start.toDateString() === end.toDateString(), which the
            // exclusive-end backend contract would otherwise
            // stretch across the day boundary in some browsers.
            start = new Date(y, m - 1, d, 0, 0, 0, 0);
            end = new Date(y, m - 1, d, 0, 0, 0, 0);
        } else {
            start = new Date(a.start_at);
            end = new Date(a.end_at);
        }
    } else {
        start = new Date(a.start_at);
        end = new Date(a.end_at);
    }
    return {
        id: a.id,
        title: isSalesAction
            ? `Sales action · ${a.title || a.action_type || ""}`.trim()
            : (a.title || a.appointment_type),
        start,
        end,
        allDay: !!a.all_day,
        resource: a,
    };
}

export default function InstallerCalendar({ shell = "installer" } = {}) {
    // CAL-001A · The Operations portal now shares this exact calendar
    // workspace. `shell="operations"` renders the same body under
    // `<OperationsShell>` instead of `<InstallerShell>` so Operations
    // users never traverse an installer-only route. The scheduling
    // API endpoints (`require_role("installer")`) already accept
    // Operations users via the built-in role superset, so no backend
    // change is required.
    const isOps = shell === "operations";
    const Shell = isOps ? OperationsShell : InstallerShell;
    const backFallback = isOps ? "/operations" : "/installer";
    const [urlParams, setUrlParams] = useSearchParams();
    const { user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const isAdmin = !!user?.is_security_admin;
    // FIELD-001C · Read the incoming URL params ONCE for the initial
    // state so `engineer_filter` / `focus` / `date` survive
    // initialisation. `parseCalendarInitFromUrl` centralises the
    // parsing rules (see unit tests in NavLinks.field001c.test.js).
    // The `[]` deps freeze this snapshot to the first-render URL —
    // subsequent URL updates come through the state→URL sync effect
    // below.
    const initialUrl = useMemo(
        () => parseCalendarInitFromUrl(urlParams),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );
    // Map the parser's neutral view tokens onto react-big-calendar's
    // Views constants so no consumer of the parser depends on the
    // library.
    const viewFromToken = (t) => (t === "day" ? Views.DAY : t === "month" ? Views.MONTH : t === "week" ? Views.WEEK : null);
    const [view, setView] = useState(() =>
        viewFromToken(initialUrl.initialView) || localStorage.getItem(VIEW_KEY) || Views.WEEK,
    );
    const [date, setDate] = useState(() => {
        if (initialUrl.initialDate) return initialUrl.initialDate;
        const v = localStorage.getItem(DATE_KEY);
        return v ? new Date(v) : new Date();
    });
    const [appointments, setAppointments] = useState([]);
    const [engineers, setEngineers] = useState([]);
    const [projects, setProjects] = useState([]);
    // CAL-001A3 · Explicit lifecycle for the Project Lead's led-project
    // picker so a direct reload where AuthContext enrichment lands
    // milliseconds after mount never leaves us with a silently blank
    // search list. `projectsState` values mirror the appointment-form
    // options states so the UI can render loading / empty / forbidden /
    // error consistently.
    const [projectsState, setProjectsState] = useState("loading");
    const [types, setTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const [filters, setFilters] = useState(() => {
        const base = { engineer_id: "", appointment_type: "", status: "", assigned: "", q: "" };
        const ef = initialUrl.engineerFilter;
        if (ef === CALENDAR_UNASSIGNED_SENTINEL) return { ...base, assigned: "false" };
        if (ef) return { ...base, engineer_id: ef };
        return base;
    });

    const [formOpen, setFormOpen] = useState(false);
    const [formInitial, setFormInitial] = useState(null);
    const [detailAppt, setDetailAppt] = useState(null);
    const [clashState, setClashState] = useState({ open: false, clashes: [], onResume: null });
    const [availState, setAvailState] = useState({ open: false, warnings: [], onResume: null });
    // CAL-001 · UK public-holiday / manual-blackout confirmation state.
    const [blackoutState, setBlackoutState] = useState({ open: false, matches: [], date: null, onResume: null });
    // CAL-001A1 · The window-scoped blackout list rendered by the
    // dedicated presentation (day tint + header chips). Never
    // enters the appointment events collection. Falls back to `[]`
    // on API failure so the calendar stays usable.
    const [blackouts, setBlackouts] = useState([]);
    // CAL-001A · Scheduling authority: Ops manages every project;
    // a Project Lead may only book on projects they lead;
    // ordinary Field Users see no New appointment control.
    const canCreateAppointment = (
        (user?.role || "").toLowerCase() === "operations"
        || (user?.role || "").toLowerCase() === "sales"
        || (user?.permissions || []).includes("manage_projects")
        || (Array.isArray(user?.leads_project_ids) && user.leads_project_ids.length > 0)
    );

    const lastFetchKey = useRef("");

    // Persist view + date
    useEffect(() => { localStorage.setItem(VIEW_KEY, view); }, [view]);
    useEffect(() => { localStorage.setItem(DATE_KEY, date.toISOString()); }, [date]);

    // Pre-open create form when routed from a task with query params
    useEffect(() => {
        const pid = urlParams.get("project_id");
        if (!pid) return;
        const dueDate = urlParams.get("due_date");
        const dueTime = urlParams.get("due_time") || "09:00";
        let startISO = new Date().toISOString();
        let endISO = new Date(Date.now() + 60 * 60 * 1000).toISOString();
        if (dueDate) {
            const start = new Date(`${dueDate}T${dueTime}:00`);
            startISO = start.toISOString();
            endISO = new Date(start.getTime() + 60 * 60 * 1000).toISOString();
            setDate(start);
        }
        setFormInitial({
            project_id: pid,
            title: urlParams.get("title") || "",
            appointment_type: urlParams.get("appointment_type") || "Site Survey",
            engineer_ids: urlParams.get("engineer_id") ? [urlParams.get("engineer_id")] : [],
            start_at: startISO,
            end_at: endISO,
        });
        setFormOpen(true);
        // Clear the params so refresh doesn't re-open
        const cleared = new URLSearchParams(urlParams);
        ["project_id", "title", "engineer_id", "due_date", "due_time", "appointment_type"].forEach((k) => cleared.delete(k));
        setUrlParams(cleared, { replace: true });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // FIELD-001C · Drill-down URL params (`engineer_filter`,
    // `focus`, `date`) are now applied inside the initial `useState`
    // initialisers above, so the earlier URL→state effect has been
    // removed. That effect used to race with the state→URL sync
    // effect and strip the incoming filter from the settled URL
    // before it could be applied (Codex UAT defect: Availability →
    // Open in calendar landed on `/operations/calendar?focus=week`,
    // dropping the resource selection on refresh).

    // FIELD-001C · Sync the resolved filter/view/date back to the URL
    // so any user-driven change (selecting a different resource in
    // the side panel, switching day↔week, navigating to another
    // date) keeps the URL canonical for refresh + Back/Forward.
    // Debounced replace to avoid history spam. `date` is only
    // written when it isn't today — otherwise the URL stays clean.
    useEffect(() => {
        let dateIso = null;
        try { dateIso = format(date, "yyyy-MM-dd"); } catch { dateIso = null; }
        const viewKey = view === Views.DAY ? "day" : view === Views.WEEK ? "week" : view === Views.MONTH ? "month" : null;
        const next = encodeCalendarUrl(urlParams, {
            engineerId: filters.engineer_id,
            assigned: filters.assigned,
            viewKey,
            dateIso,
        });
        const current = urlParams.toString();
        const proposed = next.toString();
        if (current !== proposed) {
            setUrlParams(next, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.engineer_id, filters.assigned, view, date]);

    // CAL-001A3 · Stable signature for the deps of the projects
    // bootstrap so ESLint's `react-hooks/exhaustive-deps` can
    // statically verify the array. Refires whenever the caller's
    // led-project count grows/shrinks or `engineer_id` lands.
    const leadIdsSignature = Array.isArray(user?.leads_project_ids)
        ? user.leads_project_ids.length : -1;

    // Load engineers / projects / types once
    useEffect(() => {
        // CAL-001A3 · Wait for the enriched user shape before
        // deciding which project list to fetch. On direct reload
        // ProtectedRoute already blocks on `loading=false`, but the
        // enrichment identity fields (`engineer_id`,
        // `leads_project_ids`) still commit as a distinct state
        // update — leading to a race where this effect could fire
        // with a stale user shape. Refetch every time those fields
        // change so a Lead who arrives via direct URL always ends
        // up with a populated led-project list once.
        if (!user) return;
        const isOpsRole = (user?.role || "").toLowerCase() === "operations"
            || (user?.permissions || []).includes("manage_projects");
        // Ordinary Field User with no led projects — nothing to fetch.
        const leadIds = Array.isArray(user?.leads_project_ids) ? user.leads_project_ids : [];
        if (!isOpsRole && leadIds.length === 0) {
            setProjects([]); setProjectsState("empty");
            setEngineers([]); setTypes([]);
            return;
        }
        let cancelled = false;
        setProjectsState("loading");
        (async () => {
            try {
                const engsPromise = isOpsRole
                    ? api.listEngineers({ includeArchived: true }).catch(() => [])
                    : Promise.resolve([]);
                const projsPromise = isOpsRole
                    ? api.listProjectsSummary()
                    : api.taskCreationOptions();
                const [engs, projs, ts] = await Promise.all([
                    engsPromise,
                    projsPromise,
                    api.listAppointmentTypes(),
                ]);
                if (cancelled) return;
                setEngineers(engs || []);
                const mapped = (projs || []).map((p) => ({
                    id: p.id, customer_name: p.customer_name,
                    address: p.address, system_summary: p.system_summary,
                }));
                setProjects(mapped);
                setProjectsState(mapped.length ? "ready" : "empty");
                setTypes(ts);
            } catch (e) {
                if (cancelled) return;
                const status = e?.response?.status;
                setProjects([]);
                setProjectsState(status === 403 ? "forbidden" : "error");
            }
        })();
        return () => { cancelled = true; };
        // Depend on the identity fields the endpoint uses — a
        // subsequent enrichment tick that adds `engineer_id` or
        // grows `leads_project_ids` MUST refire the fetch.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.id, user?.role, user?.permissions?.length,
        user?.engineer_id, leadIdsSignature]);

    const load = useCallback(async () => {
        const { from, to } = windowFor(view, date);
        const key = `${from}|${to}|${JSON.stringify(filters)}`;
        if (key === lastFetchKey.current) return;
        lastFetchKey.current = key;
        setLoading(true);
        setError("");
        try {
            const data = await api.listCalendarAppointments({
                from, to,
                engineer_id: filters.engineer_id || undefined,
                appointment_type: filters.appointment_type || undefined,
                status: filters.status || undefined,
                assigned: filters.assigned || undefined,
                q: filters.q || undefined,
            });
            setAppointments(data);
        } catch (e) {
            setError(formatApiError(e?.response?.data?.detail) || "Failed to load appointments");
        } finally {
            setLoading(false);
        }
    }, [view, date, filters]);

    useEffect(() => { load(); }, [load]);

    // CAL-001 · Load the combined UK public holidays + manual
    // blackouts for the visible window so the calendar can render
    // labelled all-day background entries. Uses the same window as
    // the appointments fetch so navigating (Back / Forward / date
    // picker) keeps the two in step.
    useEffect(() => {
        const { from, to } = windowFor(view, date);
        let cancelled = false;
        (async () => {
            try {
                const res = await api.listBlackouts(from, to);
                if (!cancelled) setBlackouts(Array.isArray(res?.blackouts) ? res.blackouts : []);
            } catch {
                if (!cancelled) setBlackouts([]);
            }
        })();
        return () => { cancelled = true; };
    }, [view, date]);

    const engMap = useMemo(
        () => Object.fromEntries((engineers || []).map((e) => [e.id, e])),
        [engineers]
    );

    const events = useMemo(() => appointments.map(toEvent), [appointments]);
    // CAL-001A · Blackouts are NOT appointments. Render as a
    // dedicated presentation model: (1) a day background via
    // `dayPropGetter` so the whole cell tints, and (2) a compact
    // labelled chip inside the date header of every view. No time,
    // resource, status, appointment-drawer or click affordance.
    const blackoutsByDate = useMemo(() => {
        const m = new Map();
        for (const b of (blackouts || [])) {
            if (!b?.date) continue;
            if (!m.has(b.date)) m.set(b.date, []);
            m.get(b.date).push(b);
        }
        return m;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [blackouts]);

    // CAL-001A5 · One authoritative visible-date calculation.
    // Month → the 6-week grid starting on the Monday on/before the
    // first of the month (42 local calendar days). Week → Mon–Sun of
    // the currently-focused date. Day → the single focused date.
    // Every downstream helper filters through this set so we never
    // paint a blackout from an off-screen date.
    const visibleDateKeys = useMemo(() => {
        const set = new Set();
        const push = (d) => set.add(format(d, "yyyy-MM-dd"));
        if (view === Views.DAY) {
            push(date);
        } else if (view === Views.WEEK) {
            const start = startOfWeek(date, { weekStartsOn: 1 });
            for (let i = 0; i < 7; i++) push(addDays(start, i));
        } else {
            const first = new Date(date.getFullYear(), date.getMonth(), 1);
            const gridStart = startOfWeek(first, { weekStartsOn: 1 });
            for (let i = 0; i < 42; i++) push(addDays(gridStart, i));
        }
        return set;
    }, [view, date]);

    const visibleBlackouts = useMemo(() => (
        (blackouts || []).filter((b) => b?.date && visibleDateKeys.has(b.date))
    ), [blackouts, visibleDateKeys]);
    const dayPropGetter = useCallback((d) => {
        try {
            const iso = format(d, "yyyy-MM-dd");
            if (visibleDateKeys.has(iso) && blackoutsByDate.has(iso)) {
                return { className: "pe-blackout-day", "data-testid": `cal-blackout-day-${iso}` };
            }
        } catch { /* ignore */ }
        return {};
    }, [blackoutsByDate, visibleDateKeys]);
    const BlackoutChips = useCallback(({ date: d }) => {
        try {
            const iso = format(d, "yyyy-MM-dd");
            if (!visibleDateKeys.has(iso)) return null;
            const rows = blackoutsByDate.get(iso) || [];
            if (!rows.length) return null;
            return (
                <div className="pe-blackout-chip-stack" data-testid={`cal-blackout-chip-${iso}`}>
                    {rows.map((b, i) => (
                        <span
                            key={`${b.source}-${i}`}
                            className={`pe-blackout-chip pe-blackout-chip--${b.source === "uk_public_holiday" ? "public" : "manual"}`}
                            title={`${b.name} · ${b.source === "uk_public_holiday" ? "UK public holiday" : "Manual blackout"}`}
                        >
                            {b.name}
                        </span>
                    ))}
                </div>
            );
        } catch { return null; }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [blackoutsByDate, visibleDateKeys]);
    const MonthDateHeader = useCallback(({ date: d, label }) => (
        <div className="pe-month-date">
            <span>{label}</span>
            <BlackoutChips date={d} />
        </div>
    ), [BlackoutChips]); // eslint-disable-line react-hooks/exhaustive-deps
    const TimeGridHeader = useCallback(({ date: d, label }) => (
        <div className="pe-timegrid-header">
            <div>{label}</div>
            <BlackoutChips date={d} />
        </div>
    ), [BlackoutChips]); // eslint-disable-line react-hooks/exhaustive-deps
    // DEV-019F UAT · The Unassigned tally is engineer-centric — it
    // exists to surface visits/appointments that still need a
    // resource. Sales Actions have a Sales *owner* (not an engineer
    // assignment) so must NOT contribute to this count, even when
    // no engineer_ids are present.
    const unassignedCount = useMemo(
        () => appointments.filter((a) =>
            a?.kind !== "sales_action"
            && (a.engineer_ids || []).length === 0
            && a.status !== "cancelled"
        ).length,
        [appointments]
    );

    // Renderers ----------------------------------------------------------------

    const eventPropGetter = useCallback((event) => {
        const a = event.resource;
        // DEV-019F Calendar UI completion · Sales Actions rendered
        // with a distinct visual identity so they can't be mistaken
        // for appointments/visits. Solid amber (Pearl warning band)
        // + `pe-event-sales-action` class hook for the row layout.
        //
        // DEV-019F UAT · COLOUR = TYPE OF WORK, TREATMENT = STATUS.
        // The amber TYPE colour is preserved on completed / cancelled
        // Sales Actions; only the visual TREATMENT class changes so
        // the day still communicates what happened without changing
        // the type identity.
        if (a?.kind === "sales_action") {
            const statusClass = a.status === "completed"
                ? " pe-event-completed"
                : a.status === "cancelled"
                    ? " pe-event-cancelled"
                    : "";
            return {
                className: `pe-event-sales-action${statusClass}`,
                style: {
                    background: "#F0B429",
                    color: "#131B2F",
                    border: "none",
                    fontStyle: "italic",
                },
            };
        }
        const ids = a.engineer_ids || [];
        const primary = ids.length > 0 ? engMap[ids[0]] : null;
        const colour = primary?.colour || (ids.length === 0 ? "#EEF2F6" : "#3E9E2E");
        const cls = [
            ids.length === 0 ? "pe-event-unassigned" : "",
            a.status === "cancelled" ? "pe-event-cancelled" : "",
            a.status === "completed" ? "pe-event-completed" : "",
            a.clash_override ? "pe-event-clash" : "",
        ].filter(Boolean).join(" ");
        return {
            className: cls,
            style: {
                background: ids.length === 0 ? undefined : colour,
                color: ids.length === 0 ? "#131B2F" : readableOn(colour),
                border: "none",
            },
        };
    }, [engMap]);

    const EventInner = useCallback(({ event }) => {
        const a = event.resource;
        // DEV-019F Calendar UI completion · Sales Action row layout
        // — never shows Engineer / Appointment Type / visit status.
        // Renders customer + owner instead so the user knows whose
        // to-do this is.
        if (a?.kind === "sales_action") {
            return (
                <div className="pe-event pe-event-sales-action-row"
                     data-testid={`cal-event-sales-action-${a.sales_action_id || a.id}`}>
                    <div className="pe-event-title">{event.title}</div>
                    <div className="pe-event-sub">
                        {a.customer_name || "—"}
                        {a.sales_owner_name ? ` · ${a.sales_owner_name}` : ""}
                    </div>
                </div>
            );
        }
        const ids = a.engineer_ids || [];
        const primary = ids.length > 0 ? engMap[ids[0]] : null;
        const extra = ids.length > 1 ? ` +${ids.length - 1}` : "";
        return (
            <div className="pe-event" data-testid={`cal-event-${a.id}`}>
                <div className="pe-event-title">{event.title}</div>
                <div className="pe-event-sub">
                    {a.customer_name || "—"}
                    {primary ? ` · ${primary.displayName}${extra}` : ids.length === 0 ? " · Unassigned" : ""}
                </div>
            </div>
        );
    }, [engMap]);

    // CAL-001A1 · Compose the RBC `components` prop AFTER every
    // renderer is initialised. Placing this useMemo above
    // `EventInner` triggers a temporal-dead-zone ReferenceError on
    // first mount (webpack overlay in dev, jest throw in tests).
    const calendarComponents = useMemo(() => ({
        event: EventInner,
        month: { dateHeader: MonthDateHeader },
        week:  { header: TimeGridHeader },
        day:   { header: TimeGridHeader },
    }), [EventInner, MonthDateHeader, TimeGridHeader]); // eslint-disable-line react-hooks/exhaustive-deps

    // Actions ------------------------------------------------------------------

    async function persistDragOrResize({ event, start, end }) {
        const a = event.resource;
        try {
            const saved = await api.updateCalendarAppointment(a.id, {
                start_at: start.toISOString(),
                end_at: end.toISOString(),
            });
            setAppointments((prev) => prev.map((x) => (x.id === a.id ? saved : x)));
            toast.success("Appointment updated");
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (detail?.code === "CLASH") {
                // Look up the clash details and prompt user
                try {
                    const check = await api.checkCalendarClash({
                        engineer_ids: a.engineer_ids || [],
                        start_at: start.toISOString(),
                        end_at: end.toISOString(),
                        ignore_appointment_id: a.id,
                    });
                    setClashState({
                        open: true,
                        clashes: check.clashes,
                        onResume: async () => {
                            try {
                                const saved = await api.updateCalendarAppointment(a.id, {
                                    start_at: start.toISOString(),
                                    end_at: end.toISOString(),
                                    clash_override: true,
                                });
                                setAppointments((prev) => prev.map((x) => (x.id === a.id ? saved : x)));
                                toast.success("Appointment moved (clash overridden)");
                            } catch (err) {
                                toast.error(formatApiError(err?.response?.data?.detail) || "Failed to move");
                                await load();
                            }
                        },
                    });
                } catch {
                    toast.error("Failed to move — clash detected");
                    await load();
                }
            } else {
                toast.error(formatApiError(detail) || "Failed to move appointment");
                await load();
            }
        }
    }

    const handleEventDrop = useCallback((info) => persistDragOrResize(info), [load]); // eslint-disable-line
    const handleEventResize = useCallback((info) => persistDragOrResize(info), [load]); // eslint-disable-line

    const handleSelectSlot = useCallback(({ start, end }) => {
        setFormInitial({
            start_at: start.toISOString(),
            end_at: end.toISOString(),
        });
        setFormOpen(true);
    }, []);

    const handleSelectEvent = useCallback((event) => {
        // DEV-019F Calendar UI completion · Sales Action rows never
        // open the Appointment detail modal — they navigate to the
        // project's Sales Actions section (existing DEV-020 Focus
        // route + section anchor). Role-aware so Sales users land
        // in `/sales/projects/:pid` and Ops in `/operations/...`.
        // `returnTo` preserves the exact Calendar slice for Back.
        const r = event.resource || {};
        if (r.kind === "sales_action" && r.project_id) {
            const role = (user?.role || "").toLowerCase();
            const base = role === "sales" ? "/sales/projects" : "/operations/projects";
            const returnTo = `${location.pathname}${location.search}`;
            navigate(
                `${base}/${encodeURIComponent(r.project_id)}?section=sales-actions`,
                { state: { returnTo, salesActionId: r.sales_action_id || null } },
            );
            return;
        }
        setDetailAppt(event.resource);
    }, [navigate, user?.role, location.pathname, location.search]);

    const gotoToday = () => setDate(new Date());
    const gotoPrev = () => {
        if (view === Views.DAY)   setDate((d) => addDays(d, -1));
        else if (view === Views.WEEK)  setDate((d) => addDays(d, -7));
        else                        setDate((d) => addMonths(d, -1));
    };
    const gotoNext = () => {
        if (view === Views.DAY)   setDate((d) => addDays(d, 1));
        else if (view === Views.WEEK)  setDate((d) => addDays(d, 7));
        else                        setDate((d) => addMonths(d, 1));
    };

    const resetFilters = () =>
        setFilters({ engineer_id: "", appointment_type: "", status: "", assigned: "", q: "" });

    const activeFilterCount = Object.values(filters).filter((v) => v !== "").length;

    // ─── Bulk delete (admin-only) ───────────────────────────────────────
    // Operates on the appointments currently in view (already filtered by
    // date-range + status/engineer/type/search server-side).
    const visibleAppointmentIds = useMemo(() => appointments.map((a) => a.id), [appointments]);
    const bulk = useBulkSelection(visibleAppointmentIds);
    const [bulkPanelOpen, setBulkPanelOpen] = useState(false);
    const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
    useEffect(() => {
        bulk.pruneToKnown(visibleAppointmentIds);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visibleAppointmentIds]);
    async function handleBulkDone(result) {
        reportBulkDeleteResult(result, { labelSingular: "appointment", labelPlural: "appointments" });
        bulk.clearAll();
        setBulkDeleteOpen(false);
        await refresh();
    }

    async function refresh() {
        lastFetchKey.current = "";
        await load();
    }

    // BUILD-11 · Overdue banner state for the `focus=today` experience.
    //
    // The engineer dashboard "View today's schedule" CTA routes here
    // with `?focus=today&engineer_filter=<self>`. Codex UAT reproduced
    // "1 Overdue on the dashboard, 0 appointments on the calendar" —
    // because the calendar body only renders scheduled appointments,
    // never overdue tasks. Rather than redesign the calendar we surface
    // a minimum-coherent "Overdue — needs attention" banner above the
    // calendar body whenever the focus URL param equals "today".
    // Rows link to the existing `/installer/tasks` drill-down surface
    // so we preserve the existing task details/edit journey without
    // inventing new modal machinery.
    const focusToday = urlParams.get("focus") === "today";
    const [overdueTasks, setOverdueTasks] = useState([]);
    useEffect(() => {
        if (!focusToday) { setOverdueTasks([]); return; }
        let cancelled = false;
        (async () => {
            try {
                const params = { scope: "overdue" };
                if (filters.engineer_id) params.engineer_id = filters.engineer_id;
                const rows = await api.listTasks(params);
                if (!cancelled) {
                    // Oldest due-date first — per BUILD-11 requirement.
                    const sorted = (rows || [])
                        .filter((t) => t.due_date)
                        .sort((a, b) => (a.due_date < b.due_date ? -1 : 1));
                    setOverdueTasks(sorted);
                }
            } catch { if (!cancelled) setOverdueTasks([]); }
        })();
        return () => { cancelled = true; };
    }, [focusToday, filters.engineer_id]);

    return (
        <Shell>
            <PageContainer width="full" className="pe-cal-wrap" testid="calendar-page">
                <PageHeader
                    title="Calendar"
                    icon={CalendarDays}
                    backFallback={backFallback}
                    testid="calendar-header"
                    rightSlot={canCreateAppointment ? (
                        <button
                            onClick={() => { setFormInitial(null); setFormOpen(true); }}
                            data-testid="cal-new-appointment"
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-[#3E9E2E] text-white text-sm font-medium hover:bg-[#348426]"
                        >
                            <Plus size={14} strokeWidth={2} /> New appointment
                        </button>
                    ) : null}
                />

                {/* BUILD-11 · Overdue banner rendered only when `focus=today`
                    is on the URL. Task rows link to the existing
                    `/installer/tasks?scope=overdue` drill-down surface so we
                    preserve the existing task details/edit journey. */}
                {focusToday && overdueTasks.length > 0 && (
                    <div
                        data-testid="calendar-today-overdue"
                        className="mb-4 rounded-xl border border-[#F1B0B0] bg-[#FDF2F2] p-4"
                    >
                        <div className="flex items-center justify-between gap-3 mb-2">
                            <div className="flex items-center gap-2 text-[#7A1E1E]">
                                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#DA3535] text-white text-[10px] font-semibold" data-testid="calendar-today-overdue-count">
                                    {overdueTasks.length}
                                </span>
                                <span className="text-sm font-semibold">Overdue — needs attention</span>
                            </div>
                            <a
                                href={`/installer/tasks?scope=overdue${filters.engineer_id ? `&engineer_id=${filters.engineer_id}` : ""}`}
                                data-testid="calendar-today-overdue-review"
                                className="text-sm font-medium text-[#7A1E1E] underline"
                            >
                                Review all
                            </a>
                        </div>
                        <ul className="divide-y divide-[#F1B0B0]">
                            {overdueTasks.slice(0, 5).map((t) => (
                                <li key={t.id} className="py-2 first:pt-0 last:pb-0">
                                    <a
                                        href={`/installer/tasks?scope=overdue${filters.engineer_id ? `&engineer_id=${filters.engineer_id}` : ""}`}
                                        data-testid={`calendar-today-overdue-row-${t.id}`}
                                        className="flex items-center justify-between gap-3 group"
                                    >
                                        <div className="min-w-0">
                                            <div className="text-sm font-medium text-[#131B2F] truncate group-hover:underline">{t.title}</div>
                                            <div className="text-xs text-[#4B5565] truncate">
                                                {t.project_customer_name || t.customer_name || "—"}
                                            </div>
                                        </div>
                                        <div className="text-xs text-[#7A1E1E] whitespace-nowrap">
                                            Due {t.due_date}
                                        </div>
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* View toggle + navigation toolbar — DEV-021 · one
                    cleaner row containing the view chooser, day
                    navigator and range title so the toolbar reads
                    as one deliberate control group. */}
                <div className="flex flex-wrap items-center gap-2 mb-3">
                    <div className="inline-flex items-center gap-0.5 bg-white border border-[#E5E7EB] rounded-md p-0.5" role="group" aria-label="Change view">
                        {[
                            { key: Views.DAY, label: "Day" },
                            { key: Views.WEEK, label: "Week" },
                            { key: Views.MONTH, label: "Month" },
                        ].map((v) => (
                            <button
                                key={v.key}
                                onClick={() => setView(v.key)}
                                data-testid={`cal-view-${v.label.toLowerCase()}`}
                                aria-pressed={view === v.key}
                                className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${view === v.key ? "bg-[#131B2F] text-white" : "text-[#131B2F] hover:bg-[#F5F5F7]"}`}
                            >
                                {v.label}
                            </button>
                        ))}
                    </div>

                    <div className="inline-flex items-center gap-1 ml-1">
                        <button onClick={gotoPrev} data-testid="cal-prev" aria-label="Previous period" className="w-9 h-9 flex items-center justify-center rounded-md border border-[#E5E7EB] bg-white text-[#131B2F] hover:bg-[#F5F5F7]">
                            <ChevronLeft size={16} strokeWidth={1.75} />
                        </button>
                        <button onClick={gotoNext} data-testid="cal-next" aria-label="Next period" className="w-9 h-9 flex items-center justify-center rounded-md border border-[#E5E7EB] bg-white text-[#131B2F] hover:bg-[#F5F5F7]">
                            <ChevronRight size={16} strokeWidth={1.75} />
                        </button>
                        <button onClick={gotoToday} data-testid="cal-today" className="px-3 h-9 flex items-center rounded-md border border-[#E5E7EB] bg-white text-sm font-medium text-[#131B2F] hover:bg-[#F5F5F7]">
                            Today
                        </button>
                        <input
                            type="date"
                            value={format(date, "yyyy-MM-dd")}
                            onChange={(e) => e.target.value && setDate(new Date(e.target.value))}
                            data-testid="cal-date-picker"
                            className="h-9 px-2 rounded-md border border-[#E5E7EB] bg-white text-sm text-[#131B2F]"
                        />
                    </div>

                    <div className="text-sm md:text-base font-semibold text-[#131B2F] ml-2" data-testid="cal-range-title">
                        {rangeTitle(view, date)}
                    </div>

                    <div className="flex-1" />

                    <div className="flex items-center gap-2 px-3 h-9 rounded-md border border-[#E5E7EB] bg-white min-w-[220px]">
                        <Search size={14} strokeWidth={1.75} className="text-[#4B5565]" />
                        <input
                            type="text"
                            value={filters.q}
                            onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                            placeholder="Search customer / postcode / title…"
                            data-testid="cal-search"
                            className="flex-1 min-w-0 outline-none text-sm bg-transparent text-[#131B2F]"
                        />
                        {filters.q && (
                            <button aria-label="Clear search" onClick={() => setFilters((f) => ({ ...f, q: "" }))}>
                                <X size={14} strokeWidth={1.75} className="text-[#4B5565]" />
                            </button>
                        )}
                    </div>
                </div>

                {/* Filter chips */}
                <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="cal-filters">
                    <select
                        value={filters.engineer_id}
                        onChange={(e) => setFilters((f) => ({ ...f, engineer_id: e.target.value }))}
                        data-testid="cal-filter-engineer"
                        className="h-9 px-2.5 rounded-md border border-[#E5E7EB] bg-white text-sm"
                    >
                        <option value="">All resources</option>
                        {engineers.filter((e) => e.active !== false).map((e) => (
                            <option key={e.id} value={e.id}>{e.displayName}</option>
                        ))}
                    </select>
                    <select
                        value={filters.appointment_type}
                        onChange={(e) => setFilters((f) => ({ ...f, appointment_type: e.target.value }))}
                        data-testid="cal-filter-type"
                        className="h-9 px-2.5 rounded-md border border-[#E5E7EB] bg-white text-sm"
                    >
                        <option value="">All types</option>
                        {types.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                    <select
                        value={filters.status}
                        onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
                        data-testid="cal-filter-status"
                        className="h-9 px-2.5 rounded-md border border-[#E5E7EB] bg-white text-sm"
                    >
                        <option value="">Any status</option>
                        {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                    <select
                        value={filters.assigned}
                        onChange={(e) => setFilters((f) => ({ ...f, assigned: e.target.value }))}
                        data-testid="cal-filter-assigned"
                        className="h-9 px-2.5 rounded-md border border-[#E5E7EB] bg-white text-sm"
                    >
                        <option value="">Assigned + unassigned</option>
                        <option value="true">Assigned only</option>
                        <option value="false">Unassigned only</option>
                    </select>
                    {activeFilterCount > 0 && (
                        <button
                            onClick={resetFilters}
                            data-testid="cal-filter-reset"
                            className="h-9 px-3 rounded-md border border-[#E5E7EB] bg-white text-sm hover:bg-[#F5F5F7]"
                        >
                            Reset ({activeFilterCount})
                        </button>
                    )}
                    <div className="flex-1" />
                    {unassignedCount > 0 && (
                        <button
                            onClick={() => setFilters((f) => ({ ...f, assigned: f.assigned === "false" ? "" : "false" }))}
                            data-testid="cal-unassigned-toggle"
                            className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-sm font-medium border ${
                                filters.assigned === "false"
                                    ? "bg-[#131B2F] text-white border-[#131B2F]"
                                    : "bg-white text-[#131B2F] border-[#E5E7EB] hover:bg-[#F5F5F7]"
                            }`}
                        >
                            <Users size={14} strokeWidth={1.75} />
                            Unassigned · {unassignedCount}
                        </button>
                    )}
                </div>

                {/* DEV-021 · Colour legend — reinforces the rule
                    "Colour identifies work TYPE; visual treatment
                    identifies status" without adding decorative
                    chrome. Restrained and quiet. */}
                <div
                    className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-[#4B5565]"
                    data-testid="cal-type-legend"
                    aria-label="Colour identifies work type"
                >
                    <span className="text-[10px] uppercase tracking-[0.14em] font-semibold text-[#4B5565]">
                        Type
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                        <span aria-hidden className="w-2.5 h-2.5 rounded-sm" style={{ background: "#F0B429" }} />
                        Sales Action
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                        <span aria-hidden className="w-2.5 h-2.5 rounded-sm" style={{ background: "#3E9E2E" }} />
                        Visit / Appointment
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                        <span
                            aria-hidden
                            className="w-2.5 h-2.5 rounded-sm border border-dashed border-[#94A3B8]"
                            style={{ background: "repeating-linear-gradient(45deg, #EEF2F6, #EEF2F6 2px, #DDE3EA 2px, #DDE3EA 4px)" }}
                        />
                        Unassigned
                    </span>
                    <span className="ml-1 text-[11px] italic text-[#94A3B8]">
                        Completed items are subdued
                    </span>
                </div>

                {/* Calendar body */}
                {error && (
                    <div className="mb-3 p-3 rounded-md border border-[#DA3535]/30 bg-[#FEF3F3] text-sm text-[#991B1B]" data-testid="cal-error">
                        {error} · <button className="underline" onClick={refresh}>Retry</button>
                    </div>
                )}
                <div className="relative bg-white" style={{ minHeight: 640 }}>
                    {loading && (
                        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/50" data-testid="cal-loading">
                            <span className="text-sm text-[#4B5565]">Loading calendar…</span>
                        </div>
                    )}
                    {/* CAL-001A5 · Day view uses ONE integrated
                       banner above the grid — full weekday + name +
                       type; never a detached tiny chip. Week view
                       uses a compact strip restricted to the
                       currently-displayed Mon–Sun. Month view stays
                       purely in-cell. */}
                    {view === Views.DAY && (() => {
                        const iso = format(date, "yyyy-MM-dd");
                        const rows = blackoutsByDate.get(iso) || [];
                        if (!rows.length) return null;
                        return (
                            <div className="mb-2 rounded-md border border-[#F3E1B7] bg-[#FEF6E0] px-3 py-2" data-testid="cal-day-blackout-banner">
                                {rows.map((b, i) => (
                                    <div key={`banner-${b.source}-${i}`} className="text-sm text-[#92400E]" data-testid={`cal-day-blackout-banner-line-${i}`}>
                                        <span className="font-semibold">{format(date, "EEEE d MMMM")}</span>
                                        <span className="mx-1.5 text-[#B45309]">·</span>
                                        <span>{b.name}</span>
                                        <span className="mx-1.5 text-[#B45309]">·</span>
                                        <span className="text-xs uppercase tracking-[0.06em]">
                                            {b.source === "uk_public_holiday" ? "UK public holiday" : "Manual blackout"}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        );
                    })()}
                    {view === Views.WEEK && visibleBlackouts.length > 0 && (
                        <div className="mb-2 flex flex-wrap gap-2 items-center" data-testid="cal-blackout-strip">
                            {visibleBlackouts.map((b, i) => (
                                <span
                                    key={`strip-${b.source}-${b.date}-${i}`}
                                    data-testid={`cal-blackout-strip-chip-${b.date}`}
                                    className={`pe-blackout-chip pe-blackout-chip--${b.source === "uk_public_holiday" ? "public" : "manual"}`}
                                    title={`${b.name} · ${b.source === "uk_public_holiday" ? "UK public holiday" : "Manual blackout"}`}
                                >
                                    {(() => {
                                        try {
                                            const [y, m, d] = b.date.split("-").map((x) => parseInt(x, 10));
                                            return `${format(new Date(y, m - 1, d), "EEE d MMM")} · ${b.name}`;
                                        } catch { return b.name; }
                                    })()}
                                </span>
                            ))}
                        </div>
                    )}
                    <DnDCalendar
                        localizer={localizer}
                        events={events}
                        dayPropGetter={dayPropGetter}
                        date={date}
                        view={view}
                        views={[Views.DAY, Views.WEEK, Views.MONTH]}
                        onNavigate={setDate}
                        onView={setView}
                        onEventDrop={handleEventDrop}
                        onEventResize={handleEventResize}
                        onSelectSlot={handleSelectSlot}
                        onSelectEvent={handleSelectEvent}
                        selectable
                        resizable
                        step={30}
                        timeslots={2}
                        min={new Date(1970, 0, 1, 7, 0)}
                        max={new Date(1970, 0, 1, 19, 0)}
                        eventPropGetter={eventPropGetter}
                        components={calendarComponents}
                        style={{ height: view === Views.MONTH ? 720 : 720 }}
                        popup
                        formats={{
                            timeGutterFormat: (d) => format(d, "HH:mm"),
                            eventTimeRangeFormat: ({ start, end }) => `${format(start, "HH:mm")}–${format(end, "HH:mm")}`,
                            dayHeaderFormat: (d) => format(d, "EEE d MMM"),
                            dayRangeHeaderFormat: () => rangeTitle(view, date),
                        }}
                    />
                </div>

                <div className="mt-3 text-xs text-[#4B5565]" data-testid="cal-count">
                    {appointments.length === 1
                        ? "1 appointment in view"
                        : `${appointments.length} appointments in view`}
                </div>

                {isAdmin && (
                    <div className="mt-4 border border-[#E5E7EB] rounded-xl bg-white overflow-hidden" data-testid="bulk-appointments-panel">
                        <button
                            type="button"
                            onClick={() => setBulkPanelOpen((v) => !v)}
                            className="w-full flex items-center gap-2 px-4 py-3 text-sm text-[#131B2F] hover:bg-[#F9FAFB]"
                            data-testid="bulk-appointments-panel-toggle"
                        >
                            {bulkPanelOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            <span className="font-medium">Bulk manage</span>
                            <span className="text-[#4B5565]">
                                — {appointments.length} in current view{bulk.count > 0 ? ` · ${bulk.count} selected` : ""}
                            </span>
                        </button>
                        {bulkPanelOpen && (
                            <div className="border-t border-[#E5E7EB] overflow-x-auto">
                                {appointments.length === 0 ? (
                                    <div className="p-6 text-center text-sm text-[#4B5565]">
                                        No appointments in the current filter/date range.
                                    </div>
                                ) : (
                                    <table className="w-full text-sm">
                                        <thead className="bg-[#F5F5F7] text-[11px] uppercase tracking-[0.06em] text-[#4B5565]">
                                            <tr>
                                                <th className="w-10 py-2.5 px-3 text-left">
                                                    <BulkCheckbox
                                                        checked={bulk.allVisibleSelected}
                                                        indeterminate={bulk.someVisibleSelected}
                                                        onChange={bulk.toggleAllVisible}
                                                        ariaLabel="Select all visible appointments"
                                                        testId="bulk-appointments-select-all"
                                                    />
                                                </th>
                                                <th className="py-2.5 px-3 text-left">Title</th>
                                                <th className="py-2.5 px-3 text-left hidden md:table-cell">Type</th>
                                                <th className="py-2.5 px-3 text-left hidden lg:table-cell">Start</th>
                                                <th className="py-2.5 px-3 text-left">Status</th>
                                                <th className="py-2.5 px-3 text-left hidden md:table-cell">Engineer</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {appointments.map((a) => {
                                                const engIds = a.engineer_ids || [];
                                                const engName = engIds.length === 0
                                                    ? "Unassigned"
                                                    : (engMap[engIds[0]]?.displayName || "—") + (engIds.length > 1 ? ` +${engIds.length - 1}` : "");
                                                return (
                                                    <tr key={a.id} className="border-t border-[#F3F4F6] hover:bg-[#F9FAFB]" data-testid={`bulk-appointment-row-${a.id}`}>
                                                        <td className="py-2.5 px-3">
                                                            <BulkCheckbox
                                                                checked={bulk.has(a.id)}
                                                                onChange={() => bulk.toggle(a.id)}
                                                                ariaLabel={`Select appointment ${a.title || a.appointment_type}`}
                                                                testId={`bulk-appointment-checkbox-${a.id}`}
                                                            />
                                                        </td>
                                                        <td className="py-2.5 px-3 text-[#131B2F]">{a.title || a.appointment_type}</td>
                                                        <td className="py-2.5 px-3 hidden md:table-cell text-[#4B5565]">{a.appointment_type}</td>
                                                        <td className="py-2.5 px-3 hidden lg:table-cell text-[#4B5565]">
                                                            {a.start_at ? new Date(a.start_at).toLocaleString() : "—"}
                                                        </td>
                                                        <td className="py-2.5 px-3 text-[#131B2F]">{a.status || "scheduled"}</td>
                                                        <td className="py-2.5 px-3 hidden md:table-cell text-[#4B5565]">{engName}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </PageContainer>

            {/* Modals */}
            <CalendarAppointmentForm
                open={formOpen}
                initial={formInitial}
                projects={projects}
                projectsState={projectsState}
                engineers={engineers}
                appointmentTypes={types.length ? types : ["Site Survey"]}
                onClose={async (saved) => {
                    setFormOpen(false);
                    setFormInitial(null);
                    if (saved) await refresh();
                }}
                onClashDetected={async ({ engineer_ids, start_at, end_at, ignore_appointment_id, resubmit }) => {
                    try {
                        const check = await api.checkCalendarClash({
                            engineer_ids, start_at, end_at, ignore_appointment_id,
                        });
                        setClashState({
                            open: true,
                            clashes: check.clashes,
                            onResume: async () => { await resubmit(); },
                        });
                    } catch {
                        toast.error("Failed to fetch clash details");
                    }
                }}
                onAvailabilityWarning={({ warnings, resubmit }) => {
                    setAvailState({
                        open: true,
                        warnings: warnings || [],
                        onResume: async () => { await resubmit(); },
                    });
                }}
                onBlackoutWarning={({ matches, date, resubmit }) => {
                    setBlackoutState({
                        open: true,
                        matches: matches || [],
                        date,
                        onResume: async () => { await resubmit(); },
                    });
                }}
            />

            <CalendarAppointmentDetail
                open={!!detailAppt}
                appointment={detailAppt}
                engineers={engineers}
                onClose={() => setDetailAppt(null)}
                onEdit={() => {
                    setFormInitial(detailAppt);
                    setDetailAppt(null);
                    setFormOpen(true);
                }}
                onRefresh={refresh}
            />

            <ClashWarningDialog
                open={clashState.open}
                clashes={clashState.clashes}
                onCancel={() => setClashState({ open: false, clashes: [], onResume: null })}
                onOverride={async () => {
                    const fn = clashState.onResume;
                    setClashState({ open: false, clashes: [], onResume: null });
                    if (fn) await fn();
                    await refresh();
                }}
            />

            <AvailabilityWarningDialog
                open={availState.open}
                warnings={availState.warnings}
                onCancel={() => setAvailState({ open: false, warnings: [], onResume: null })}
                onOverride={async () => {
                    const fn = availState.onResume;
                    setAvailState({ open: false, warnings: [], onResume: null });
                    if (fn) await fn();
                    await refresh();
                }}
            />

            <BlackoutConfirmationDialog
                open={blackoutState.open}
                matches={blackoutState.matches}
                date={blackoutState.date}
                onCancel={() => setBlackoutState({ open: false, matches: [], date: null, onResume: null })}
                onConfirm={async () => {
                    const fn = blackoutState.onResume;
                    setBlackoutState({ open: false, matches: [], date: null, onResume: null });
                    if (fn) await fn();
                    await refresh();
                }}
            />

            {isAdmin && (
                <>
                    <BulkActionBar
                        count={bulk.count}
                        labelSingular="appointment"
                        labelPlural="appointments"
                        onClear={bulk.clearAll}
                        onDelete={() => setBulkDeleteOpen(true)}
                        testidPrefix="bulk-appointments"
                    />
                    <BulkDeleteModal
                        open={bulkDeleteOpen}
                        onCancel={() => setBulkDeleteOpen(false)}
                        onDone={handleBulkDone}
                        ids={bulk.ids}
                        labelSingular="appointment"
                        labelPlural="appointments"
                        deleter={(batch, ctx) => api.bulkDeleteAppointments(batch, ctx)}
                        extraWarning="Notifications linked to each appointment will also be removed. Related tasks are not affected."
                        testidPrefix="bulk-appointments-delete"
                    />
                </>
            )}
        </Shell>
    );
}
